import {CloudDownload, X} from 'lucide-react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert';
import {IconButton} from '@/components/ui/icon-button';
import {Skeleton} from '@/components/ui/skeleton';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {graphqlClient} from '@/services/graphqlClient';
import {useGetMedicinalProductQuery} from '@/services/graphqlHooks';

import MedicinalProductDialog from './MedicinalProductDialog';

type Props = {
  productId: string;
  onRemove: () => void;
};

function LinkedMedicinalProduct({productId, onRemove}: Props) {
  const {t} = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {data, isFetching} = useGetMedicinalProductQuery(
    graphqlClient,
    {productId},
    {enabled: !!productId}
  );

  const product = data?.getMedicinalProduct;

  return (
    <>
      <Alert className="pr-10">
        {isFetching ? (
          <div className="col-start-2 flex flex-col gap-1.5">
            <Skeleton className="h-4 w-40 rounded-full" />
            <Skeleton className="h-3 w-28 rounded-full" />
          </div>
        ) : (
          <>
            <CloudDownload />
            <AlertTitle
              className="cursor-pointer hover:underline"
              onClick={() => setIsDialogOpen(true)}
            >
              {product?.productName}
            </AlertTitle>
            {product?.marketingAuthorizationHolder && (
              <AlertDescription>
                {product.marketingAuthorizationHolder}
              </AlertDescription>
            )}
          </>
        )}
        <TooltipWrapper title={t('Remove connection')}>
          <IconButton
            variant="tertiary"
            size="xsmall"
            type="button"
            className="absolute top-2 right-2"
            onClick={onRemove}
          >
            <X size={12} />
            <span className="sr-only">{t('Remove connection')}</span>
          </IconButton>
        </TooltipWrapper>
      </Alert>
      <MedicinalProductDialog
        productId={productId}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </>
  );
}

export default LinkedMedicinalProduct;
